// The Azure loan-origination platform built at FM Home Loans, drawn from the
// experience entry for that role. Every node here maps to something in that
// list of responsibilities — nothing is invented for the diagram's sake.

export const architecture = {
  title: "Loan origination platform",
  company: "FM Home Loans",
  label: "One example",
  summary:
    "The loan origination platform I built at FM Home Loans — React on the front, Azure Functions and Logic Apps behind it, and a document pipeline that merged hundreds of files a day.",
  // Laid out on a 1000 x 560 canvas.
  nodes: [
    {
      id: "spa",
      label: "React SPA",
      sub: "Redux · Router",
      x: 60, y: 220, w: 150, h: 74,
      group: "client",
      detail:
        "The loan-application front end: React with Redux and Router, built and deployed through CI/CD to Azure blob storage as a static site.",
      stack: ["React", "Redux", "React Router", "CI/CD"],
    },
    {
      id: "auth0",
      label: "Auth0",
      sub: "JWT · Azure AD",
      x: 60, y: 60, w: 150, h: 74,
      group: "identity",
      detail:
        "Identity provider for the platform. JWTs on the client, verified server-side to authenticate and authorise every request. I set up the custom rules, email templates, login pages and an enterprise connection for Azure AD sign-in.",
      stack: ["Auth0", "JWT", "Azure AD", "OIDC"],
    },
    {
      id: "frontdoor",
      label: "Azure Front Door",
      sub: "proxy · routing",
      x: 290, y: 220, w: 160, h: 74,
      group: "edge",
      detail:
        "The proxy layer in front of everything. Routed traffic between the static front end and the API surface behind it.",
      stack: ["Azure Front Door"],
    },
    {
      id: "functions",
      label: "Azure Functions",
      sub: ".NET Core APIs",
      x: 530, y: 220, w: 165, h: 74,
      group: "compute",
      detail:
        "The backend. C# and .NET Core Web APIs written against S.O.L.I.D. principles, running serverless on Azure Functions.",
      stack: ["Azure Functions", ".NET Core", "C#", "S.O.L.I.D."],
    },
    {
      id: "logic",
      label: "Logic Apps",
      sub: "schedules · webhooks",
      x: 530, y: 60, w: 165, h: 74,
      group: "compute",
      detail:
        "Dozens of time-based business operations plus HTTP webhooks for third-party integrations, driven by cloud queues and event triggers.",
      stack: ["Logic Apps", "Queues", "Event triggers", "Webhooks"],
    },
    {
      id: "docs",
      label: "Document service",
      sub: "merge · e-signature",
      x: 530, y: 400, w: 165, h: 74,
      group: "compute",
      detail:
        "Merged hundreds of documents with loan data every day and drove the e-signature process through a third-party provider.",
      stack: ["C#", "E-signature API", "Batch processing"],
    },
    {
      id: "sql",
      label: "SQL Server",
      sub: "EF · procs · triggers",
      x: 790, y: 220, w: 150, h: 74,
      group: "data",
      detail:
        "Schemas designed with Entity Framework on .NET Core — stored procedures, functions, triggers and views, with scripted nightly backups.",
      stack: ["SQL Server", "Entity Framework", "Stored procedures", "Views"],
    },
    {
      id: "graph",
      label: "Microsoft Graph",
      sub: "OneDrive · Outlook",
      x: 790, y: 60, w: 150, h: 74,
      group: "external",
      detail:
        "An internal React app authenticated with Azure AD, integrating OneDrive, Outlook and other Microsoft services through the Graph API.",
      stack: ["Graph API", "Azure AD", "OneDrive", "Outlook"],
    },
    {
      id: "blob",
      label: "Blob storage",
      sub: "static host · files",
      x: 790, y: 400, w: 150, h: 74,
      group: "data",
      detail:
        "Stored the generated documents and hosted the built front end — deploys landed here straight from the CI/CD pipeline.",
      stack: ["Azure Blob Storage", "CI/CD"],
    },
  ],
  // from → to, with `dashed` for control-plane rather than request-path links.
  edges: [
    { from: "spa", to: "auth0", dashed: true },
    { from: "spa", to: "frontdoor" },
    { from: "frontdoor", to: "functions" },
    { from: "functions", to: "auth0", dashed: true },
    { from: "functions", to: "sql" },
    { from: "functions", to: "docs" },
    { from: "logic", to: "functions" },
    { from: "logic", to: "graph" },
    { from: "docs", to: "blob" },
  ],
};

// Accent for the parts he owned; neutral for everything he integrated with.
export const GROUP_COLORS = {
  client: "var(--accent)",
  compute: "var(--accent)",
  data: "var(--accent)",
  edge: "var(--text-soft)",
  identity: "var(--text-soft)",
  external: "var(--text-soft)",
};
